import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LangSwitcher } from '../LangSwitcher';

// ─── Btn ─────────────────────────────────────────────────────────────────────

type BtnVariant = 'primary' | 'green' | 'outline' | 'ghost' | 'soft' | 'softGreen' | 'danger';
type BtnSize = 'sm' | 'md' | 'lg' | 'xl';

interface BtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  as?: 'button' | 'a';
  href?: string;
  target?: string;
  rel?: string;
}

const BTN_SIZES: Record<BtnSize, string> = {
  sm: 'h-8 px-3.5 text-[13px]',
  md: 'h-10 px-5 text-sm',
  lg: 'h-12 px-6 text-[15px]',
  xl: 'h-14 px-8 text-base',
};

const BTN_VARIANTS: Record<BtnVariant, string> = {
  primary:
    'bg-brand-blue-500 text-white hover:bg-brand-blue-600 shadow-[0_8px_20px_-8px_rgba(31,127,191,0.6)]',
  green:
    'bg-brand-green-500 text-white hover:bg-brand-green-600 shadow-[0_8px_20px_-8px_rgba(71,149,35,0.6)]',
  outline:
    'border border-ink-200 dark:border-ink-700 text-ink-700 dark:text-ink-100 hover:bg-ink-100 dark:hover:bg-ink-800/60',
  ghost:
    'text-ink-700 dark:text-ink-100 hover:bg-ink-150 dark:hover:bg-ink-800/60',
  soft:
    'bg-brand-blue-50 text-brand-blue-600 hover:bg-brand-blue-100 dark:bg-brand-blue-900/40 dark:text-brand-blue-100',
  softGreen:
    'bg-brand-green-50 text-brand-green-600 hover:bg-brand-green-100 dark:bg-brand-green-900/40 dark:text-brand-green-100',
  danger:
    'bg-rose-500/10 text-rose-600 hover:bg-rose-500/20 dark:bg-rose-500/15 dark:text-rose-300',
};

export const Btn: React.FC<BtnProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  as: As = 'button',
  ...props
}) => {
  const cls = `inline-flex items-center justify-center gap-2 font-semibold rounded-full transition select-none ${BTN_SIZES[size]} ${BTN_VARIANTS[variant]} ${className}`;
  if (As === 'a') {
    const { href, target, rel } = props as any;
    return (
      <a href={href} target={target} rel={rel} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button className={cls} {...props}>
      {children}
    </button>
  );
};

// ─── Tag ─────────────────────────────────────────────────────────────────────

type TagTone = 'neutral' | 'blue' | 'green' | 'outline';

interface TagProps {
  tone?: TagTone;
  className?: string;
  children: React.ReactNode;
}

const TAG_TONES: Record<TagTone, string> = {
  neutral: 'bg-ink-150 text-ink-700 dark:bg-ink-800 dark:text-ink-200',
  blue: 'bg-brand-blue-50 text-brand-blue-600 dark:bg-brand-blue-900/40 dark:text-brand-blue-100',
  green: 'bg-brand-green-50 text-brand-green-600 dark:bg-brand-green-900/40 dark:text-brand-green-100',
  outline: 'border border-ink-200 dark:border-ink-700 text-ink-600 dark:text-ink-300',
};

export const Tag: React.FC<TagProps> = ({ tone = 'neutral', className = '', children }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${TAG_TONES[tone]} ${className}`}
  >
    {children}
  </span>
);

// ─── Card ────────────────────────────────────────────────────────────────────

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padded?: boolean;
  hover?: boolean;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  padded = true,
  hover = false,
  onClick,
}) => (
  <div
    onClick={onClick}
    className={
      `rounded-2xl bg-white dark:bg-ink-900 border border-ink-150 dark:border-ink-800 shadow-card dark:shadow-cardDark ` +
      (padded ? 'p-5 ' : '') +
      (hover ? 'transition hover:-translate-y-0.5 hover:shadow-pop cursor-pointer ' : '') +
      className
    }
  >
    {children}
  </div>
);

// ─── Avatar ──────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#3695d3', '#5ab331', '#d97757', '#7b6cf6', '#e0a020',
];

interface AvatarProps {
  seed?: string;
  src?: string;
  size?: number;
  anonymous?: boolean;
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  seed = 'U',
  src,
  size = 40,
  anonymous = false,
  className = '',
}) => {
  if (src) {
    return (
      <img
        src={src}
        alt={seed}
        style={{ width: size, height: size }}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    );
  }
  if (anonymous) {
    return (
      <div
        style={{ width: size, height: size }}
        className={`shrink-0 rounded-full bg-ink-150 dark:bg-ink-800 flex items-center justify-center text-ink-400 dark:text-ink-500 ${className}`}
      >
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none">
          <path
            d="M9 9a3 3 0 1 1 4.83 2.39c-.86.65-1.83 1.36-1.83 2.6V15"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="18.5" r="1.2" fill="currentColor" />
        </svg>
      </div>
    );
  }
  const idx = ((seed.charCodeAt(0) || 0) + (seed.charCodeAt(1) || 0)) % AVATAR_COLORS.length;
  const color = AVATAR_COLORS[idx];
  return (
    <div
      style={{ width: size, height: size, backgroundColor: color + '26', color }}
      className={`shrink-0 rounded-full overflow-hidden flex items-center justify-center font-bold ${className}`}
    >
      <span style={{ fontSize: size * 0.42, lineHeight: 1 }} className="relative">
        {seed[0]?.toUpperCase()}
      </span>
    </div>
  );
};

// ─── QGlyph ──────────────────────────────────────────────────────────────────

export const QGlyph: React.FC<{ tone?: 'blue' | 'green'; size?: number }> = ({
  tone = 'blue',
  size = 28,
}) => (
  <div
    style={{ width: size, height: size }}
    className={`shrink-0 rounded-lg flex items-center justify-center font-extrabold text-white ${
      tone === 'blue' ? 'bg-brand-blue-500' : 'bg-brand-green-500'
    }`}
  >
    <span style={{ fontSize: size * 0.5, lineHeight: 1 }}>
      {tone === 'blue' ? 'A' : 'Q'}
    </span>
  </div>
);

// ─── Toggle ──────────────────────────────────────────────────────────────────

interface ToggleProps {
  value: boolean;
  onChange: (v: boolean) => void;
  title: string;
  description?: React.ReactNode;
  accent?: 'green' | 'blue';
}

export const Toggle: React.FC<ToggleProps> = ({
  value,
  onChange,
  title,
  description,
  accent = 'green',
}) => (
  <div className="flex items-start gap-4 py-3.5 px-4 rounded-2xl bg-ink-100/60 dark:bg-ink-850/60 hover:bg-ink-100 dark:hover:bg-ink-850 transition">
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-[14px]">{title}</div>
      {description && (
        <div className="text-[12px] text-ink-500 dark:text-ink-400 mt-1 leading-relaxed">
          {description}
        </div>
      )}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`relative w-12 h-7 rounded-full transition shrink-0 mt-0.5 ${
        value
          ? accent === 'blue'
            ? 'bg-brand-blue-500'
            : 'bg-brand-green-500'
          : 'bg-ink-300 dark:bg-ink-700'
      }`}
    >
      <span
        className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-all ${
          value ? 'right-0.5' : 'left-0.5'
        }`}
      />
    </button>
  </div>
);

// ─── ServerBadge ─────────────────────────────────────────────────────────────

interface ServerInfoData { name: string; favicon: string; }
const _siCache = new Map<string, ServerInfoData>();

const useServerInfo = (host: string): ServerInfoData => {
  const [info, setInfo] = useState<ServerInfoData>(
    _siCache.get(host) ?? { name: host, favicon: `https://${host}/favicon.ico` }
  );
  useEffect(() => {
    if (_siCache.has(host)) { setInfo(_siCache.get(host)!); return; }
    fetch(`/api/v1/server/info/${encodeURIComponent(host)}`)
      .then(r => r.ok ? r.json() : null)
      .then((d: ServerInfoData | null) => {
        if (d) { _siCache.set(host, d); setInfo(d); }
      })
      .catch(() => {});
  }, [host]);
  return info;
};

export const ServerBadge: React.FC<{ host: string; size?: 'sm' | 'md'; className?: string }> = ({
  host,
  size = 'sm',
  className = '',
}) => {
  const small = size === 'sm';
  const { name, favicon } = useServerInfo(host);
  const [imgOk, setImgOk] = useState(true);

  return (
    <span
      className={
        `inline-flex items-center rounded-full bg-ink-100 dark:bg-ink-850 border border-ink-150 dark:border-ink-800 select-none ` +
        (small ? 'h-5 pl-1 pr-2 gap-1 text-[10.5px]' : 'h-6 pl-1.5 pr-2.5 gap-1.5 text-[11.5px]') +
        ' ' + className
      }
    >
      {imgOk ? (
        <img
          src={favicon}
          alt=""
          width={small ? 12 : 14}
          height={small ? 12 : 14}
          className="shrink-0 rounded-[2px] object-contain"
          onError={() => setImgOk(false)}
        />
      ) : (
        <span
          style={{ width: small ? 12 : 14, height: small ? 12 : 14, fontSize: (small ? 12 : 14) * 0.65 }}
          className="shrink-0 rounded-[2px] bg-ink-300 dark:bg-ink-600 inline-flex items-center justify-center text-white font-bold"
        >
          {(host[0] || '?').toUpperCase()}
        </span>
      )}
      <span className="text-ink-700 dark:text-ink-200 font-mono">{name}</span>
    </span>
  );
};

// ─── QTypeBadge ──────────────────────────────────────────────────────────────

export const QTypeBadge: React.FC<{ type?: 'direct' | 'public'; target?: string; size?: 'sm' | 'md' }> = ({
  type = 'direct',
  target = '',
  size = 'sm',
}) => {
  const { t } = useTranslation();
  const small = size === 'sm';
  const cls = small ? 'h-5 px-2 text-[10.5px] gap-1' : 'h-6 px-2.5 text-[11.5px] gap-1.5';
  const iconSize = small ? 11 : 13;
  if (type === 'public') {
    return (
      <span
        className={`inline-flex items-center rounded-full font-semibold bg-brand-green-50 text-brand-green-700 dark:bg-brand-green-900/40 dark:text-brand-green-100 ${cls}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width={iconSize} height={iconSize} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="8" r="3"/>
          <path d="M3 20c0-3 3-5 6-5s6 2 6 5"/>
          <circle cx="17" cy="9" r="2.4"/>
          <path d="M14.5 20c0-2 1.5-4 5-4s4 2 4 4"/>
        </svg>
        {t('nav.community')}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold bg-brand-blue-50 text-brand-blue-700 dark:bg-brand-blue-900/40 dark:text-brand-blue-100 ${cls}`}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width={iconSize} height={iconSize} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="9" r="3.5"/>
        <path d="M5 20c0-4 3-6 7-6s7 2 7 6"/>
      </svg>
      {t('ui.toTarget', { target })}
    </span>
  );
};

// ─── Icons ───────────────────────────────────────────────────────────────────

type SVGProps = React.SVGAttributes<SVGElement>;

export const Icons = {
  Home: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v9h14v-9"/>
    </svg>
  ),
  Compass: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="9"/><path d="M15 9l-2 4-4 2 2-4 4-2z"/>
    </svg>
  ),
  Globe: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/>
    </svg>
  ),
  Bell: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M6 8a6 6 0 1 1 12 0c0 6 2 7 2 7H4s2-1 2-7Z"/><path d="M10 19a2 2 0 0 0 4 0"/>
    </svg>
  ),
  Search: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
    </svg>
  ),
  Plus: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 5v14M5 12h14"/>
    </svg>
  ),
  Heart: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z"/>
    </svg>
  ),
  Boost: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M7 7h10l-3-3M17 17H7l3 3"/>
    </svg>
  ),
  Reply: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M9 14H6a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3h-4l-4 4v-4z"/>
    </svg>
  ),
  Share: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7"/><path d="M16 6l-4-4-4 4"/><path d="M12 2v14"/>
    </svg>
  ),
  Sparkle: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6"/>
    </svg>
  ),
  Lock: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>
    </svg>
  ),
  Check: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="m4 12 5 5L20 6"/>
    </svg>
  ),
  Link: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M10 14a4 4 0 0 0 5.6 0l3-3a4 4 0 0 0-5.6-5.6L11.5 7"/>
      <path d="M14 10a4 4 0 0 0-5.6 0l-3 3a4 4 0 0 0 5.6 5.6L12.5 17"/>
    </svg>
  ),
  ChevronLeft: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="m15 18-6-6 6-6"/>
    </svg>
  ),
  Trash: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/>
    </svg>
  ),
  Flag: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
    </svg>
  ),
  ExternalLink: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M14 4h6v6"/><path d="M20 4 10 14"/><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/>
    </svg>
  ),
  Mastodon: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M21 9.4c0-4.2-2.7-5.5-2.7-5.5C16.9 3.2 14.5 3 12 3h-.1c-2.5 0-5 .2-6.3.9C5.6 3.9 3 5.2 3 9.4c0 1 0 2 .1 3 .2 4 .9 8 4.4 9 1.7.4 3.1.5 4.3.4 2-.1 3.2-.7 3.2-.7l-.1-1.6s-1.4.5-3.1.4c-1.7-.1-3.4-.2-3.7-2.3 0-.2 0-.4-.1-.6 3.4.8 6.3.4 7.1.3 2.2-.3 4-1.7 4.3-3 .4-2 .4-5 .4-5zm-3 5.1h-1.9V9.8c0-1-.4-1.5-1.3-1.5s-1.3.6-1.3 1.6v2.6h-1.9V9.8c0-1-.4-1.5-1.3-1.5s-1.3.6-1.3 1.6v3l-1.9.1V9.6c0-1 .3-1.8.8-2.4.5-.6 1.3-.9 2.2-.9 1.1 0 1.9.4 2.4 1.3l.5.7.5-.7c.5-.9 1.3-1.3 2.4-1.3.9 0 1.7.3 2.2.9.5.6.8 1.4.8 2.4v4.9z"/>
    </svg>
  ),
  Misskey: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M4 18V8a2 2 0 0 1 3.5-1.3L12 12l4.5-5.3A2 2 0 0 1 20 8v10h-3v-7l-3.5 4h-3L7 11v7H4z"/>
    </svg>
  ),
  Settings: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Logout: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  Sun: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
    </svg>
  ),
  Moon: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  ),
  Computer: (p: SVGProps) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
    </svg>
  ),
};

// ─── ThemeToggle ─────────────────────────────────────────────────────────────

export const ThemeToggle: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  const toggle = () => {
    const next = !dark;
    setDark(next);
    if (next) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  return (
    <button
      onClick={toggle}
      title={dark ? t('ui.switchToLight') : t('ui.switchToDark')}
      className={`w-8 h-8 rounded-full flex items-center justify-center text-ink-500 dark:text-ink-400 hover:bg-ink-100 dark:hover:bg-ink-800 transition ${className}`}
    >
      {dark
        ? <Icons.Sun width={16} height={16} />
        : <Icons.Moon width={16} height={16} />}
    </button>
  );
};

// ─── Sidebar ─────────────────────────────────────────────────────────────────

interface SidebarProps {
  activeKey?: string;
  onNavigate?: (key: string) => void;
  unansweredCount?: number;
  userInfo?: any;
  onGoToBox?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeKey = 'mybox',
  onNavigate,
  unansweredCount = 0,
  onGoToBox,
}) => {
  const { t } = useTranslation();
  const nav = [
    { key: 'community', label: t('nav.community'), icon: Icons.Globe },
    { key: 'mybox', label: t('common.myQuestionBox'), icon: Icons.Home, count: unansweredCount },
    { key: 'settings', label: t('common.settings'), icon: Icons.Settings },
  ];
  return (
    <div className="h-full p-5 flex flex-col gap-1.5">
      <div className="px-3 pt-1 pb-5">
        <img src="/fediqb_banner_dark.svg" alt="FediQB" className="h-7 select-none dark:hidden" />
        <img src="/fediqb_banner.svg" alt="FediQB" className="h-7 select-none hidden dark:block" />
      </div>
      {nav.map((n) => {
        const Cmp = n.icon;
        const active = n.key === activeKey;
        return (
          <button
            key={n.key}
            onClick={() => onNavigate?.(n.key)}
            className={
              `flex items-center justify-between px-4 h-12 rounded-2xl text-[15px] font-medium transition ` +
              (active
                ? 'bg-brand-blue-50 text-brand-blue-700 dark:bg-brand-blue-900/40 dark:text-brand-blue-100'
                : 'text-ink-600 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-900')
            }
          >
            <span className="flex items-center gap-3">
              <Cmp width="20" height="20" /> {n.label}
            </span>
            {n.count != null && n.count > 0 && (
              <span className="bg-brand-green-500 text-white text-[11px] font-bold rounded-full px-2 h-5 inline-flex items-center">
                {n.count}
              </span>
            )}
          </button>
        );
      })}
      <div className="mt-auto pt-3 flex flex-col gap-2">
        <div className="flex justify-end px-1 items-center gap-1">
          <LangSwitcher />
          <ThemeToggle />
        </div>
        {onGoToBox && (
          <button
            onClick={onGoToBox}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-2xl bg-brand-blue-500 text-white text-[14px] font-semibold hover:bg-brand-blue-600 transition shadow-[0_4px_14px_-4px_rgba(31,127,191,0.55)]"
          >
            <Icons.Plus width={16} height={16} /> {t('common.receiveQuestions')}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── MobileNav ───────────────────────────────────────────────────────────────

export const MobileNav: React.FC<SidebarProps> = ({
  activeKey = 'mybox',
  onNavigate,
  unansweredCount = 0,
  onGoToBox,
}) => {
  const { t } = useTranslation();
  const navItems = [
    { key: 'community', label: t('nav.community'), icon: Icons.Globe },
    { key: 'mybox', label: t('common.questionBox'), icon: Icons.Home, count: unansweredCount },
    { key: 'settings', label: t('common.settings'), icon: Icons.Settings },
  ];
  return (
    <nav className="fixed bottom-0 inset-x-0 z-30 bg-white dark:bg-ink-900 border-t border-ink-150 dark:border-ink-800 flex">
      {navItems.map((n) => {
        const Cmp = n.icon;
        const active = n.key === activeKey;
        return (
          <button
            key={n.key}
            onClick={() => onNavigate?.(n.key)}
            className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition ${
              active ? 'text-brand-blue-600 dark:text-brand-blue-300' : 'text-ink-500 dark:text-ink-400'
            }`}
          >
            <div className="relative">
              <Cmp width={22} height={22} />
              {n.count != null && n.count > 0 && (
                <span className="absolute -top-1.5 -right-2.5 bg-brand-green-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 inline-flex items-center justify-center px-0.5">
                  {n.count > 99 ? '99+' : n.count}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium">{n.label}</span>
          </button>
        );
      })}
      {onGoToBox && (
        <button
          onClick={onGoToBox}
          className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-brand-blue-600 dark:text-brand-blue-300"
        >
          <div className="w-[34px] h-[34px] rounded-full bg-brand-blue-500 flex items-center justify-center shadow-[0_2px_8px_-2px_rgba(31,127,191,0.5)]">
            <Icons.Plus width={16} height={16} className="text-white" />
          </div>
          <span className="text-[10px] font-medium">{t('common.receiveQuestions')}</span>
        </button>
      )}
    </nav>
  );
};

// ─── PublicHeader ────────────────────────────────────────────────────────────

interface PublicHeaderProps {
  loggedIn?: boolean;
  /** sm以上で中央に表示するコンテンツ（匿名状態など） */
  centerContent?: React.ReactNode;
  /** 「戻る」リンクの遷移先 */
  backTo?: string;
  /** 「戻る」リンクのラベル */
  backLabel?: string;
}

export const PublicHeader: React.FC<PublicHeaderProps> = ({
  loggedIn = false,
  centerContent,
  backTo,
  backLabel,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resolvedBackLabel = backLabel ?? t('common.back');
  const navBtnCls =
    'h-8 px-3 rounded-full text-[13px] font-medium text-ink-600 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800 flex items-center gap-1.5 transition';
  return (
    <header className="flex items-center justify-between px-6 md:px-10 py-4 border-b border-ink-150 dark:border-ink-800 bg-white/80 dark:bg-ink-900/80 backdrop-blur-sm sticky top-0 z-10">
      <Link to="/" className="shrink-0">
        <img src="/fediqb_banner_dark.svg" alt="FediQB" className="h-6 select-none dark:hidden" />
        <img src="/fediqb_banner.svg" alt="FediQB" className="h-6 select-none hidden dark:block" />
      </Link>

      {centerContent && (
        <div className="hidden sm:flex items-center gap-2 text-[13px] text-ink-500 dark:text-ink-400">
          {centerContent}
        </div>
      )}

      <div className="flex items-center gap-0.5">
        {backTo && (
          <>
            <Link to={backTo} className={navBtnCls}>
              <Icons.Home width={14} height={14} />
              <span className="hidden sm:inline">{resolvedBackLabel}</span>
            </Link>
            <div className="w-px h-4 bg-ink-200 dark:bg-ink-700 mx-1" />
          </>
        )}
        <LangSwitcher />
        <ThemeToggle />
        {loggedIn ? (
          <>
            <button onClick={() => navigate('/community')} className={navBtnCls}>
              <Icons.Globe width={14} height={14} />
              <span className="hidden sm:inline">{t('nav.community')}</span>
            </button>
            <button onClick={() => navigate('/mypage')} className={navBtnCls}>
              <Icons.Home width={14} height={14} />
              <span className="hidden sm:inline">{t('common.mypage')}</span>
            </button>
          </>
        ) : (
          <button onClick={() => navigate('/login')} className={navBtnCls}>
            {t('common.login')}
          </button>
        )}
      </div>
    </header>
  );
};

// ─── LoadingScreen ────────────────────────────────────────────────────────────

export const LoadingScreen: React.FC<{ message?: string }> = ({
  message,
}) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 dark:bg-ink-950">
      <div className="flex flex-col items-center gap-3 text-ink-400 dark:text-ink-500">
        <div className="w-8 h-8 rounded-full border-2 border-ink-200 dark:border-ink-700 border-t-brand-blue-400 animate-spin" />
        <span className="text-[14px]">{message ?? t('common.loading')}</span>
      </div>
    </div>
  );
};

// ─── ErrorScreen ──────────────────────────────────────────────────────────────

export const ErrorScreen: React.FC<{
  title?: string;
  message?: string;
  onBack?: () => void;
}> = ({ title, message, onBack }) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-ink-50 dark:bg-ink-950 text-ink-800 dark:text-ink-100">
      <div className="w-14 h-14 rounded-full bg-rose-50 dark:bg-rose-900/30 flex items-center justify-center">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26" className="text-rose-500">
          <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
        </svg>
      </div>
      <h2 className="text-xl font-bold">{title ?? t('common.error')}</h2>
      {message && <p className="text-ink-500 dark:text-ink-400 text-[14px]">{message}</p>}
      {onBack && (
        <button
          onClick={onBack}
          className="mt-2 text-[14px] text-brand-blue-500 hover:underline"
        >
          ← {t('common.back')}
        </button>
      )}
    </div>
  );
};

// ─── Toast ───────────────────────────────────────────────────────────────────

export const useToast = () => {
  const [message, setMessage] = React.useState('');

  const show = React.useCallback((msg: string, duration = 2500) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), duration);
  }, []);

  const ToastEl = message ? (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-ink-800 dark:bg-ink-100 text-white dark:text-ink-900 px-5 py-3 rounded-2xl shadow-pop text-[13px] font-medium whitespace-nowrap pointer-events-none animate-fade-in">
      {message}
    </div>
  ) : null;

  return { show, ToastEl };
};

